import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookOpen, CheckCircle2, Search, History, Flame } from "lucide-react";

export default function Learn() {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({ completed_lessons: [], section_progress: {} });
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("All");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/learn/lessons").then((r) => setLessons(r.data)).catch(() => {});
    if (user) {
      api.get("/learn/progress").then((r) => setProgress(r.data)).catch(() => {});
      api.get("/learn/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user]);

  const done = new Set(progress.completed_lessons || []);
  const resumed = progress.section_progress || {};

  const subjects = useMemo(() => {
    const s = new Map();
    lessons.forEach((l) => s.set(l.subject || "General", (s.get(l.subject || "General") || 0) + 1));
    return ["All", ...Array.from(s.keys()).sort()];
  }, [lessons]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return lessons.filter((l) => {
      if (subject !== "All" && (l.subject || "General") !== subject) return false;
      if (t && !`${l.title} ${l.domain} ${l.subject}`.toLowerCase().includes(t)) return false;
      return true;
    });
  }, [lessons, q, subject]);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="learn-page">
      <p className="eyebrow mb-2">Learn</p>
      <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">The Axioms Library</h1>
      <p className="mt-3 max-w-2xl text-slate-600">{lessons.length} lessons from the Mahakali Studios knowledge base, grouped into subject shelves with live-typeset mathematics and progress tracking.</p>

      {(() => {
        const rl = lessons.find((l) => l.id === progress.last_active_lesson);
        if (!rl) return null;
        return (
          <Link to={`/learn/${rl.id}`} data-testid="resume-banner" className="axiom-card mt-6 flex items-center justify-between gap-4 border-[#E61A27]/30 bg-[#FFF5F5] p-5">
            <div className="flex items-center gap-3">
              <History className="h-5 w-5 text-[#E61A27]" />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#B8860B]">Continue where you left off</p>
                <p className="font-display text-lg text-slate-900">{rl.title}</p>
              </div>
            </div>
            <span className="btn-crimson !py-2 text-sm">Resume</span>
          </Link>
        );
      })()}

      {user && stats && (
        <div className="axiom-card mt-6 p-6" data-testid="progress-panel">
          <div className="flex flex-wrap items-center gap-8">
            <div className="flex items-center gap-3">
              <Flame className="h-8 w-8 text-[#E61A27]" />
              <div><p className="font-display text-2xl text-slate-900" data-testid="streak-count">{stats.streak}</p><p className="text-xs uppercase tracking-wider text-slate-400">day streak</p></div>
            </div>
            <div><p className="font-display text-2xl text-slate-900">{stats.longest}</p><p className="text-xs uppercase tracking-wider text-slate-400">longest</p></div>
            <div><p className="font-display text-2xl text-slate-900">{stats.total_completed}/{stats.total_lessons}</p><p className="text-xs uppercase tracking-wider text-slate-400">chapters done</p></div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            {stats.shelves.filter((s) => s.total > 0).slice(0, 8).map((s) => (
              <Link key={s.subject} to={`/learn/shelf/${encodeURIComponent(s.subject)}`} data-testid={`shelf-row-${s.subject.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group">
                <div className="flex items-center justify-between text-xs"><span className="text-slate-600 group-hover:text-[#E61A27]">{s.subject}</span><span className="text-slate-400">{s.completed}/{s.total}</span></div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[#D4AF37]" style={{ width: `${s.total ? (s.completed / s.total) * 100 : 0}%` }} /></div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="relative mt-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input data-testid="lesson-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search topics (e.g. Limits, Topology, Lean)…"
          className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 outline-none focus:border-[#E61A27]" />
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="subject-chips">
        {subjects.map((s) => (
          <button key={s} data-testid={`chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setSubject(s)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${subject === s ? "border-[#E61A27] bg-[#E61A27] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#D4AF37]"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((l) => (
          <Link to={`/learn/${l.id}`} key={l.id} data-testid={`lesson-card-${l.id}`} className="axiom-card group flex flex-col p-6">
            <div className="flex items-center justify-between">
              <BookOpen className="h-6 w-6 text-[#E61A27]" />
              {done.has(l.id) ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                : resumed[l.id] ? <span className="inline-flex items-center gap-1 text-xs text-[#B8860B]" data-testid={`resume-${l.id}`}><History className="h-4 w-4" /> Resume</span> : null}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11px] font-semibold text-[#B8860B]">{l.subject || l.domain}</span>
              <span className="text-[11px] font-mono text-slate-400">{l.difficulty}</span>
            </div>
            <h3 className="mt-3 font-display text-xl text-slate-900">{l.title}</h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500 line-clamp-3">{l.summary}</p>
          </Link>
        ))}
      </div>
      {filtered.length === 0 && <p className="mt-10 text-slate-500" data-testid="learn-empty">No lessons match your filters.</p>}
    </div>
  );
}
